import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Alert } from "@cmsgov/ds-medicare-gov";
import { RouteComponentProps, withRouter } from "react-router";
import { PacePlan } from "../../@types";
import { getPacePlansForState } from "../../api";
import {
  AppPage,
  ExclamationNote,
  FocusableHeader,
  LoadingMask,
  PrintButton,
  StateSelector,
} from "../../components";
import { makePlanDisplayId, useTranslate } from "../../helpers";
import PacePlanCard from "./PacePlanCard";
import states from "../../helpers/states";
import Pagination from "../../components/Pagination";
import { AppContext } from "../../app/store";
import { AnalyticsActionType } from "../../app/contexts/Analytics/types";

interface PacePlansMatchParams {
  state: string;
}

const PAGE_SIZE = 10;

const PacePlansPage: FunctionComponent<RouteComponentProps> = ({ match }) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const [plans, setPlans] = useState<PacePlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stateAbbr, setStateAbbr] = useState<string>(
    (match.params as PacePlansMatchParams).state
  );
  const [page, setPage] = useState(0);
  const topOfList = useRef<HTMLParagraphElement>(null);

  const fetchPlans = async () => {
    setLoading(true);
    setError("");
    setPage(0);
    try {
      const plans = await getPacePlansForState(stateAbbr);
      setLoading(false);
      setPlans(plans);
    } catch (_e) {
      setError(t("pace.plans.error"));
      setLoading(false);
    }
  };

  const scrollToTopOfList = () => {
    topOfList.current && topOfList.current.focus();
  };

  useEffect(() => {
    fetchPlans();
  }, [stateAbbr]);

  useEffect(() => {
    setStateAbbr((match.params as PacePlansMatchParams).state);
  }, [match.params]);

  useEffect(() => {
    if (plans.length) {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "Results Page Number",
          event_label: `${stateAbbr}:${page + 1}`,
          other_props: {
            event_category: "PACE",
          },
        },
      });
    }
  }, [page]);

  const offset = page * PAGE_SIZE;
  const plansToShow = plans.slice(offset, offset + PAGE_SIZE);

  return (
    <AppPage hideLogin={true} innerClassName="PacePlansPage">
      <LoadingMask loading={loading} delay={500} />
      <div className="PacePlansPage__banner ds-u-fill--gray-lightest ds-u-display--flex ds-u-justify-content--between ds-u-flex-wrap--wrap mct-hide-print ds-u-padding--2 ds-u-md-padding-x--7 ds-u-md-padding-y--3 ds-u-margin-bottom--2">
        <StateSelector
          buttonText={t("pace.state_selector.button_text")}
          defaultLabel={t("pace.state_selector.select_state")}
          defaultValue={stateAbbr}
          label={t("pace.state_selector.select_state")}
          programType="PACE"
        />
        <PrintButton
          tealiumProps={{
            event_action: "Print",
            event_label: window.location.hash,
            other_props: { event_category: "PACE" },
          }}
        />
      </div>
      <div className="PacePlansPage__content">
        {error && (
          <div className="ds-u-margin-bottom--2">
            <Alert variation="error" role="alert">
              {error}
            </Alert>
          </div>
        )}
        {error || plans.length == 0 ? (
          <p>{t("pace.plans.error.select_state")}</p>
        ) : (
          <div ref={topOfList} tabIndex={-1}>
            <FocusableHeader
              className="PacePlansPage__h1"
              text={{
                id: "pace.plans.showing_num_plans_in_state",
                values: {
                  pageRange: `${offset + 1} - ${offset +
                    Math.min(plansToShow.length, PAGE_SIZE)}`,
                  numTotalPlans: plans.length.toString(),
                  stateName:
                    states.find(s => s.abbreviation === stateAbbr)?.name || "",
                },
              }}
            />
          </div>
        )}
        {!error && !!plans.length && (
          <p>
            <ExclamationNote>
              {t("pace.plans.organization_plans_note.part_1")} <br />
              {t("pace.plans.organization_plans_note.part_2")}
            </ExclamationNote>
          </p>
        )}
        <ul
          className="ds-c-list--bare"
          role="list"
          data-testid="e2e-plan-results"
        >
          {plansToShow.map(plan => {
            const planId = makePlanDisplayId(plan);
            return (
              <li key={planId} role="listitem">
                <PacePlanCard pacePlan={plan} />
              </li>
            );
          })}
        </ul>
        {plans.length > PAGE_SIZE && (
          <div className="ds-u-margin-top--3">
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(plans.length / PAGE_SIZE)}
              totalResults={plans.length}
              onPageSelect={(page: number): void => {
                setPage(page);
                scrollToTopOfList();
              }}
            />
          </div>
        )}
      </div>
    </AppPage>
  );
};

export default withRouter(PacePlansPage);
